<template>
  <div class="cda-content-body" ref="cdaContentBody" :key='getKey' >
    <!-- What the document left undated or uncoded, and what may be done about it
         before Save (D6 default 2026-10-21: notes, not blockers). -->
    <cda-staged-item-notes />
    <div class="form-group" v-if="!isCdaSaved">
      <!-- Save commits the C-CDA's extracted items into the chart, so it waits
           until the side bar shows them for review (login/cda
           getCdaSaveUnavailableReason), and then nothing the document left
           out holds it back: an undated or uncoded item is a note on the item,
           above, not a blocker. Deleting the upload needs no review. -->
      <div class="tooltip-wrapper" :title="saveUnavailableText || ''">
        <button class="mr-md-2 mr-2 save-cda btn btn-primary" @click="handleSaveCdaById(cdaId)" ref="saveCdaBtn" data-test="save-cda" :disabled="saveUnavailable !== null">Save CDA</button>
      </div>
      <div class="tooltip-wrapper">
        <button class="save-cda btn btn-danger" @click="handleDeleteCdaById(cdaId)" ref="deleteCdaBtn">Delete</button>
      </div>
      <p v-if="saveUnavailable !== null" class="save-unavailable" data-test="save-cda-unavailable">
        {{ saveUnavailableText }}
        <button v-if="scanTimedOut" type="button" class="btn btn-link check-again" data-test="cda-scan-check-again" @click="checkScanAgain">Check again</button>
        <!-- A C-CDA whose extracted items were discarded has been read and has
             nothing staged; reading it again stages a fresh set. -->
        <button v-if="canReadAgain" type="button" class="btn btn-link check-again" data-test="cda-read-again" :disabled="readingAgain" @click="readAgain">Read this CDA again</button>
        <!-- Why the last try failed: the notes that carry a review change's error
             (CdaStagedItemNotes) are not on screen for a C-CDA with no staged set. -->
        <span v-if="canReadAgain && readAgainError" class="read-again-error" role="alert" data-test="cda-read-again-error">{{ readAgainError }}</span>
      </p>
    </div>

    <button class="btn download-btn" @click="handleDownLoadCda">Download CDA</button>

    <div class="row">
      <cda-doc :cdaDoc="cda" @on-cda-load='onCdaLoad'></cda-doc>
      <cda-content-side-bar 
        :key='getKey2'
        :itemInfo='itemInfo'
        :ready='ready'>
      </cda-content-side-bar>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import CdaDoc from '@/components/includes/cda/CdaDoc';
import CdaStagedItemNotes from '@/components/includes/cda/CdaStagedItemNotes';
// import CdaContentSideBar from '@/components/includes/cda/CdaContentSideBar';
import CdaContentSideBar from '@/components/includes/cda/CdaContentSideBarGrouped';
import {
  downloadCdaById,
  deleteCdaById
} from '@/api/cdaApi';
import { CdasNavigation } from '@/store/modules/fsm/messages';
import { useChartCdasStore } from '@/stores/chartCdas';
import { useChartCdaStagedItemsStore, ReviewChangeNotSaved } from '@/stores/chartCdaStagedItems';
import { PRIVILEGES } from '@/constants/privileges';
import { usePrivilegesStore } from '@/stores/privileges';
import router from '@/router/index';
import download from 'downloadjs';
import { h } from 'vue';

// A C-CDA still `scanning` is re-checked this often, this many times: two
// minutes. Past that the page says it is taking longer than usual and offers to
// check again.
const SCAN_CHECK_INTERVAL_MS = 3000;
const SCAN_CHECK_LIMIT = 40;

export default {
  name: 'cda-content-body',
  components: {
    'cda-doc': CdaDoc,
    'cda-content-side-bar': CdaContentSideBar,
    'cda-staged-item-notes': CdaStagedItemNotes,
  },
  computed: {
    ready() {
      return this.loaded && this.mounted;
    },
    ...mapGetters('login', {
      user: 'getUser'
    }),
    ...mapGetters('login/cda', {
      cdaList: 'getCdaList',
      cdaIndex: 'getCdaIndex',
      cdaId: 'getCdaId',
      isCdaSaved: 'isCdaSaved',
      saveUnavailable: 'getCdaSaveUnavailableReason',
      openCdaStatus: 'getOpenCdaStatus',
      indexOfLastCda: 'getIndexOfLastCda',
      cdaListLoaded: 'isCdaListLoaded',
      cdaListLoadFailed: 'cdaListLoadFailed',
      reviewNotes: 'getOpenCdaReviewNotes'
    }),
    getKey() {
      return 'cda-content-body-'+(this.$route.params.index || 'null');
    },
    getKey2() {
      return 'cda-content-body2-'+(this.$route.params.index || 'null');
    },
    itemInfo() {
      if(this.loaded)
        return { id:this.id,type:this.type };
      else
        return null;
    },
    /** The uid of the row this page's index names in the chart's list; null until the list has it. */
    openRowId() {
      const row = this.cdaList[this.index];
      return row && row.id !== null && row.id !== undefined ? row.id : null;
    },
    /**
     * What decides which C-CDA the page shows: the row its index names, and
     * whether the chart's list has been read. The list is read after the page
     * renders (a reload of /cdas/N, or a chart whose list nobody has read), so
     * the row can arrive after `created`.
     */
    openTarget() {
      return `${this.cdaListLoaded && !this.cdaListLoadFailed}|${this.openRowId}`;
    },
    /**
     * What the re-check of a C-CDA still being read follows: which C-CDA is
     * open, its import state, and the chart's list rows. The rows are a new
     * array after every re-read, so a C-CDA that is still `scanning` once the
     * list answers arms the next check although its status has not changed
     * (the status alone, a string, would not call the watcher again).
     */
    scanWatch() {
      return [this.cdaId, this.openCdaStatus, useChartCdasStore().items];
    },
    /**
     * The open C-CDA is still being read: its row says `scanning`, or it said
     * so last and the list's latest re-read failed. A failed re-read empties
     * the list, which leaves the C-CDA with no status at all; stopping there
     * ended the checks for good, and nothing picked up `staged`.
     */
    stillBeingRead() {
      if (this.openCdaStatus === 'scanning') return true;
      return this.openCdaStatus === null
        && this.cdaListLoadFailed
        && this.lastScanningId !== null
        && this.lastScanningId === this.cdaId;
    },
    /** The checks ran out while the C-CDA was still being read. */
    scanTimedOut() {
      return this.stillBeingRead && this.scanChecks >= SCAN_CHECK_LIMIT;
    },
    /**
     * The open C-CDA has no staged items (its set was discarded) and nothing of
     * it was saved: it may be read again, by anyone who may write documents.
     */
    canReadAgain() {
      return !this.isCdaSaved
        && this.openCdaStatus === 'uploaded'
        && usePrivilegesStore().effectiveGranted(PRIVILEGES.UPLOAD_DOCUMENTS);
    },
    /**
     * What the last try to read the C-CDA again said when it failed, for the
     * page to say beside its button. A failed review write is kept in the staged
     * items store (`errors`), and the one other place that shows it,
     * CdaStagedItemNotes, is not on screen for a C-CDA with no staged set, which
     * is the C-CDA this button is for: without this the button came back and
     * nothing said the try had failed.
     */
    readAgainError() {
      return useChartCdaStagedItemsStore().errors[this.cdaId] || '';
    },
    saveUnavailableText() {
      if (this.scanTimedOut) {
        return 'This CDA is still being read, and it is taking longer than usual. Check again, or open it again later to review its extracted items.';
      }
      if (this.stillBeingRead) {
        return 'This CDA is still being read. Its extracted items will appear here for review when it is done.';
      }
      return this.saveUnavailable;
    }
  },
  props:
  {
    index: {
      type: Number,
      required: true
    },
    type: {
      type: String,
      required: false
    },
    id: {
      type: Number,
      required: false
    },
  },
  data() {
    return {
      cda: '',
      loaded: false,
      mounted: false,
      // The re-check of a C-CDA still being read; see scanWatch below.
      scanCheck: null,
      scanChecks: 0,
      // The uid last seen `scanning` while open (stillBeingRead).
      lastScanningId: null,
      // The C-CDA last asked for, and which ask is current: an answer for an
      // earlier one is dropped (refresh).
      requestedId: null,
      loadToken: 0,
      // "Does not exist" is said once for an index, not on every re-read.
      missingShown: false,
      // A re-read of the C-CDA (POST scan) is under way.
      readingAgain: false,
    }
  },
  mounted() {
    this.mounted = true;
  },
  methods: {
    onCdaLoad() {
      this.loaded = true;
    },
    stopScanCheck() {
      if (this.scanCheck !== null) clearTimeout(this.scanCheck);
      this.scanCheck = null;
    },
    /** Arm the next check if the open C-CDA is still being read and checks remain. */
    armScanCheck() {
      this.stopScanCheck();
      if (!this.stillBeingRead || this.scanChecks >= SCAN_CHECK_LIMIT) return;
      this.scanCheck = setTimeout(() => this.checkScan(), SCAN_CHECK_INTERVAL_MS);
    },
    /** Remember whether the open C-CDA was last seen being read (stillBeingRead). */
    noteScanStatus() {
      if (this.openCdaStatus === 'scanning') {
        this.lastScanningId = this.cdaId;
      } else if (this.openCdaStatus !== null || this.lastScanningId !== this.cdaId) {
        this.lastScanningId = null;
      }
    },
    /**
     * Mark the chart's C-CDA list stale; `login/getAllData` re-reads it. Its
     * answer, a new set of rows, calls the scanWatch watcher, which arms the
     * next check while the row still says `scanning`. Nothing is armed while
     * the read is under way, so a slow answer is never abandoned by the next
     * check's invalidation.
     */
    checkScan() {
      this.scanCheck = null;
      this.scanChecks += 1;
      useChartCdasStore().invalidate();
    },
    /**
     * Read the open C-CDA again (its extracted items were discarded). It is
     * `scanning` until the new set is staged, which the checks above wait for
     * with a fresh two minutes.
     */
    async readAgain() {
      if (this.readingAgain) return;

      this.readingAgain = true;

      try {
        if (await this.readCdaAgain()) {
          this.stopScanCheck();
          this.scanChecks = 0;
        }
      } finally {
        this.readingAgain = false;
      }
    },
    /** After the checks ran out: another two minutes, starting now. */
    checkScanAgain() {
      this.stopScanCheck();
      this.scanChecks = 0;
      this.checkScan();
    },
    ...mapActions('login', [
      'updateApplicationModel'
    ]),
    ...mapActions('login/cda', [
      'requestCdaById',
      'saveCdaById',
      'clearCda',
      'readCdaAgain'
    ]),
    ...mapActions('login/watch',[
      'setExternalMeasuresLoaded'
    ]),
    handleSaveCdaById(id) {
      // The disabled button is the visible rule; this is the same rule for a
      // call that does not come through it.
      if (this.saveUnavailable !== null) return;
      // Where the review's failures stand as Save is pressed. A change that fails
      // while the clinician is deciding is one they have not seen - the dialog is
      // over the notes that would say so - and Save must not commit over it.
      const failuresBefore = useChartCdaStagedItemsStore().failureMark(id);
      // Items the document left undated or uncoded go in as they are (main's
      // Save commits every item); say so, so the clinician chooses with it in view.
      const flagged = this.reviewNotes.length;
      const asTheyStand = flagged === 0 ? '' : ` Items with no date or no code this system knows (${flagged}) are saved as they stand.`;

      this.$swal({
        title: 'Save CDA',
        text: 'Save current CDA to your profile?' + asTheyStand,
        type: 'warning',
        showCancelButton: true,
        cancelButtonColor: '#d33',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Yes, save it',
        reverseButtons: true,
      }).then((result) => {
        if (result.value && !this.isCdaSaved) {
          let loader = this.$loading.show({
            isFullPage: true
          },{
            after: () => h('div',{},'Saving CDA...')
          });

          // Save commits what the API holds, so it waits for every change to an
          // item that is still on its way (a date being sent, an item being left
          // out), and not at all over one that failed since the button was pressed
          // (login/cda saveCdaById waits too, for a caller that is not this page).
          useChartCdaStagedItemsStore().settled(id, failuresBefore)
          .then((worked) => (worked ? this.saveCdaById(id) : Promise.reject(new ReviewChangeNotSaved())))
          .then(()=>{
            this.$swal({
              type: 'success',
              title: 'CDA saved',
              text: 'CDA is saved to your profile.'
            });
            this.setExternalMeasuresLoaded(false);
          })
          .catch((err) => {
            console.error(err);
            // Save waited for a change the clinician made to an item, and it failed
            // (login/cda saveCdaById): nothing was committed, the reason is above
            // the items, and trying the same Save again is not the fix.
            if (err && err.code === 'review_change_failed') {
              this.$swal({
                type: 'error',
                title: 'Not saved yet',
                text: 'A change you made to an extracted item could not be saved, so the CDA was not saved. The reason is shown with the items. Fix or undo that change, then save again.',
              });

              return;
            }
            this.$swal({
              type: 'error',
              title: 'Unable to save',
              text: 'Please try again or upload another CDA',
            })
          })
          .finally( () => {
            loader.hide();
            loader = null;
          });
        }
      });
    },
    handleDeleteCdaById(id) {
      let loader;
      this.$swal({
        title: 'Delete CDA',
        text: 'Are you sure  you want to delete this CDA?',
        type: 'warning',
        showCancelButton: true,
        cancelButtonColor: '#3085d6',
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it',
        reverseButtons: true,
      }).then((result) => {
        if (result.value && !this.isCdaSaved) {
          loader = this.$loading.show({
            container: this.$refs.pageBody
          },{
            after: () => h('div',{},`Deleting CDA...`)
          });

          deleteCdaById(id)
          .then((response) => {
            if (response.data) {
              // return to get to next then
              return;
            } else {
              this.$swal({
                type: 'error',
                title: 'Error',
                text: 'Cannot delete this CDA',
              });
            }
          })
          .then(() => {
            loader.hide();
            loader = this.$loading.show({
              container: this.$refs.pageBody
            },{
              after: () => h('div',{},`Updating application data...`)
            });
            return this.updateApplicationModel();
          })
          .then(() => {
            return this.clearCda();
          })
          .then(() => {
            router.push({ name: CdasNavigation });
          })
          .catch((err) => {
            this.$swal({
                type: 'error',
                title: 'Error',
                text: `Cannot delete this CDA. Error: ${err}`,
              });
          })
          .finally(() => {
            loader.hide();
            loader = null;
          });
        }
      });

    },
    handleDownLoadCda() {
      downloadCdaById(this.cdaId)
      .then((response) => {
        download(response.data, `cda_${this.cdaIndex}_${this.user.userName}.xml`, response.headers['content-type']);
      })
      .catch((err) => {
        alert(`Failed to download cda. Reason: ${JSON.stringify(err)}`);
      });
    },
    /**
     * Show the C-CDA this page's index names, once the chart's list has it.
     *
     * Until the list has been read there is nothing to show and nothing to
     * say: `openTarget`'s watcher calls this again when it arrives. A list that
     * could not be read is said on the page (CdaPage.vue); neither is a file
     * that does not exist. Re-reads of the list that keep the same row do not
     * fetch the C-CDA again.
     */
    open() {
      const id = this.openRowId;

      if (id !== null) {
        if (id !== this.requestedId) this.refresh();
        return;
      }
      if (!this.cdaListLoaded || this.cdaListLoadFailed || this.missingShown) return;

      this.missingShown = true;
      this.$swal({
        type: 'error',
        title: `Unable to retrieve`,
        text: `The file you requested does not exist. Please choose another file.`
      })
    },
    refresh () {
      const id = this.openRowId;
      if (id === null) return;

      // display progress (spinning wheel) as a visual cue
      let loader = this.$loading.show({
        }, {
          after: () => h('div',{},`Retrieving CDA...`)
        });
      // update cda in iframe
      // Only the latest ask opens its C-CDA (login/cda) and shows its bytes;
      // an earlier one answering late is dropped, so the document on screen,
      // the side bar and Save all name the same C-CDA.
      const token = ++this.loadToken;
      const isCurrent = () => token === this.loadToken;
      let index = this.index;
      this.requestedId = id;
      this.requestCdaById({id, index, isCurrent})
      .then((cdaDoc) => {
        // this change of data will trigger the watcher in cda-doc
        if (isCurrent()) this.cda = cdaDoc;
      })
      .catch((err) => {
        if (!isCurrent()) return;
        console.error(err);
        this.$swal({
          type: 'error',
          title: `Unable to fetch cda: ${err}`,
          text: 'Please try again',
        })
      }).finally( () => {
        loader.hide();
        loader = null;
      });
    }
  },
  watch: {
    // refresh div to get cda content when cda index is changed in url
    // The C-CDA opened in its place is checked afresh, once it is open (its
    // uid changes scanWatch).
    index (newIndex) {
      this.stopScanCheck();
      this.scanChecks = 0;
      this.missingShown = false;
      this.open();
    },
    // The row the index names, or the list's arrival (see open()).
    openTarget() {
      this.open();
    },
    // The port reads a C-CDA in a queued job after upload (main read it inside
    // the upload request), so the page can open one still `scanning`. Its list
    // row is re-read every 3 s, for up to two minutes, until it says `staged`;
    // its extracted items then load into the side bar.
    scanWatch: {
      immediate: true,
      handler() {
        this.noteScanStatus();
        this.armScanCheck();
      },
    },
  },
  // Main's `beforeDestroy` clean-up, under the hook Vue 3 calls. The app runs
  // the plain Vue 3 runtime (vue.config.js aliases `vue` to its esm-bundler
  // build; @vue/compat is only the template compiler's mode), which never calls
  // `beforeDestroy`: the re-check timer outlived the page, and the open C-CDA
  // stayed open in `login/cda` (its staged items re-read on other pages).
  beforeUnmount() {
    this.stopScanCheck();
    // A C-CDA still on its way does not open after the page has gone.
    this.loadToken += 1;
    this.clearCda();
    delete this.cda;
    this.cda = null;
    this.loaded = false;
    this.mounted = false;
  },
  created () {
    this.open();
  }
}
</script>

<style scoped lang="scss">
  $delete: #ff4444;
  $delete-hover: #cc0000;
  .download-btn {
    margin-bottom: 1em;
  }
  .save-unavailable {
    margin: 0.5rem 0 0;
    font-size: 0.85rem;
    color: #6c757d;

    .check-again {
      padding: 0;
      font-size: inherit;
      vertical-align: baseline;
    }

    .read-again-error {
      display: block;
      margin-top: 0.25rem;
      color: #c0392b;
    }
  }
  .btn {
    font-size: 0.9rem;
  }
  input[type="text"] {
    font-size: 0.9rem;
  }

  .tooltip-wrapper {
    display: inline-block;

    .btn[disabled] {
      cursor: not-allowed;
    }
  }

  .hidden {
    display: none;
  }
</style>
